import { useCallback, useEffect, Fragment } from 'react';

import { getSearchedLocation } from '@zola-helpers/client/dist/es/marketplace/vendorSearchUtils';
import { CtaType } from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import { deleteLocalStorage, getLocalStorage } from '@zola-helpers/client/dist/es/util/storage';

import { MiniQuizPrompt } from '~/components/common/modals/MiniQuizPromptModal/MiniQuizPrompt';
import UnsuppliedMarketModal from '~/components/common/modals/UnsuppliedMarketModal';
import { useModal } from '~/contexts/ModalContext';
import { useLaunchMiniQuizFromUrl } from '~/hooks/useLaunchMiniQuizFromUrl';
import {
  useOnboardedViaFavoritedStorefront,
  VENDOR_FAVORITE_ONBOARD_STOREFRONT_UUID_KEY,
} from '~/hooks/useOnboardedViaFavoritedStorefront';
import { useScrollToVendorCard } from '~/hooks/useScrollToVendorCard';
import { SessionStorageToasts } from '~/hooks/useToastsFromLocalStorage';
import { setSearchedLocation, removeSearchedLocation, isSearchLocation } from '~/util/searchUtils';
import { trackCtaClicked, TrackingProductLocation } from '~/util/trackingHelper';

import { useSearchResultsPageContext } from './components/SearchResults/contexts/SearchResultsPageContext';

const SearchResultsPageModalsAndToasts = (): JSX.Element => {
  const recordedLocationFromLocalStorage = getSearchedLocation();

  const {
    vendorSearch,
    loading,
    searchResults: { totalHits },
  } = useSearchResultsPageContext();

  useOnboardedViaFavoritedStorefront({
    showCustomInitialToasts: false,
    location: TrackingProductLocation.SEARCH_RESULTS,
    shouldDeleteStorefrontUuidFromLocalStorage: false,
  });

  useLaunchMiniQuizFromUrl();

  const favoritedStorefrontUuid = getLocalStorage(VENDOR_FAVORITE_ONBOARD_STOREFRONT_UUID_KEY);

  useScrollToVendorCard({ ready: !loading, favoritedStorefrontUuid });

  const { hideModal, showCustomModal } = useModal();
  const { selectedLocation } = vendorSearch;

  const showRealWeddings = useCallback(() => {
    if (isSearchLocation(selectedLocation)) {
      trackCtaClicked({
        ctaId: 'View Real Weddings',
        location: TrackingProductLocation.UNSUPPLIED_MARKET_MODAL,
        position: 1,
        ctaType: CtaType.BUTTON,
      });
      window.location.assign(`/inspiration/real-weddings/${selectedLocation.slug}`);
    }
    hideModal();
  }, [hideModal, selectedLocation]);

  useEffect(() => {
    const showUnsuppliedModal = getLocalStorage('SHOW_UNSUPPLIED_MODAL');
    const showModal = showUnsuppliedModal ? JSON.parse(showUnsuppliedModal) : false;

    if (showModal) {
      if (totalHits === 0 && selectedLocation?.type === 'CITY') {
        const marketName = selectedLocation.label;
        showCustomModal(UnsuppliedMarketModal, {
          showRealWeddings,
          marketName,
        });
      }

      deleteLocalStorage('SHOW_UNSUPPLIED_MODAL');
    }
  }, [selectedLocation, showCustomModal, showRealWeddings, totalHits]);

  useEffect(() => {
    if (isSearchLocation(selectedLocation)) {
      /**
       * if there is a location in the URL, then update localStorage
       * this ensures the localStorage is updated even when a user visits
       * the SRP directly with a link
       */

      setSearchedLocation(selectedLocation);
    } else {
      /**
       * if there is no location in the URL, then clear localStorage
       * this will prevent the breadcrumbs in the SLP from showing the searched
       * location and instead show the vendor's home market
       */
      removeSearchedLocation();
    }
  }, [selectedLocation, recordedLocationFromLocalStorage]);

  return (
    <Fragment>
      <MiniQuizPrompt />
      {/* // Must be on the client */}
      <SessionStorageToasts />
    </Fragment>
  );
};

export default SearchResultsPageModalsAndToasts;
